/**
 * Session return doors after a task or composer hop.
 *
 * `returnTo` is the page the person was on when they opened the work.
 * Completing or Back must land there — never a retired alias, an open
 * redirect, or a book-wide list when a more specific parent exists.
 */

const LOCAL_ORIGIN = 'https://statura.local';

function parseInternal(href: string): URL | null {
  if (!href.startsWith('/') || href.startsWith('//')) return null;
  try {
    const parsed = new URL(href, LOCAL_ORIGIN);
    if (parsed.origin !== LOCAL_ORIGIN) return null;
    return parsed;
  } catch {
    return null;
  }
}

function clientTabHref(clientId: string, tab: string, hash?: string): string {
  const panel = hash ? `&panel=${encodeURIComponent(hash)}` : '';
  const fragment = hash ? `#${hash}` : '';
  return `/clients/${clientId}?tab=${tab}${panel}${fragment}`;
}

function remapRetiredPath(parsed: URL): string | null {
  const path = parsed.pathname.replace(/\/+$/, '') || '/';
  if (path === '/home') return `/overview${parsed.search}${parsed.hash}`;

  const clientSah = path.match(/^\/clients\/([^/]+)\/sah$/);
  if (clientSah?.[1]) return clientTabHref(clientSah[1], 'sah-hub');

  const clientCarePlans = path.match(/^\/clients\/([^/]+)\/care-plans$/);
  if (clientCarePlans?.[1]) return clientTabHref(clientCarePlans[1], 'care-plans');

  const nestedPlan = path.match(
    /^\/care-plans\/([^/]+)\/(clinical|risks|preferences|interventions|worker_instructions|agreements|reviews)$/,
  );
  if (nestedPlan?.[1] && nestedPlan[2]) {
    return `/care-plans/${nestedPlan[1]}/compose#${nestedPlan[2]}`;
  }

  if (path === '/roster') {
    const clientId = parsed.searchParams.get('clientId');
    return clientId
      ? `/roster/dispatch?view=clients&clientId=${encodeURIComponent(clientId)}`
      : '/roster/dispatch';
  }

  if (path === '/home-care/exceptions') {
    return '/roster/visit-exceptions';
  }

  return null;
}

function isAllowedPath(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (
    path === '/' ||
    path === '/overview' ||
    path === '/tasks' ||
    path === '/tasks/all' ||
    path === '/care-plans' ||
    path === '/assessments' ||
    path === '/clients' ||
    path === '/people'
  ) {
    return true;
  }
  if (/^\/tasks\/[^/]+$/.test(path) && path !== '/tasks/new') return true;
  if (/^\/clients\/[^/]+$/.test(path)) return true;
  if (/^\/clients\/[^/]+\/(assessments|medications)$/.test(path)) return true;
  if (/^\/care-plans\/[^/]+$/.test(path)) return true;
  if (/^\/care-plans\/[^/]+\/compose$/.test(path)) return true;
  if (/^\/care-plans\/[^/]+\/goals\/[^/]+$/.test(path)) return true;
  if (/^\/assessments\/[^/]+$/.test(path)) return true;
  if (path === '/roster/dispatch' || path.startsWith('/roster/dispatch/')) return true;
  if (path === '/roster/visit-exceptions' || path.startsWith('/roster/visit-exceptions/')) {
    return true;
  }
  if (path === '/roster/time-review' || path.startsWith('/roster/time-review/')) return true;
  if (path.startsWith('/home-care/') && path !== '/home-care/exceptions') return true;
  if (path.startsWith('/people/')) return true;
  if (path.startsWith('/me/')) return true;
  if (path.startsWith('/compliance/')) return true;
  if (path.startsWith('/finance/')) return true;
  return false;
}

/** Book-wide lists — too vague when a client or task parent is known. */
export function isGenericListReturnHref(href: string): boolean {
  const parsed = parseInternal(href);
  if (!parsed) return true;
  const path = parsed.pathname.replace(/\/+$/, '') || '/';
  return path === '/care-plans' || path === '/clients' || path === '/assessments';
}

/**
 * Allowlisted in-app return door. Remaps retired aliases onto live doors.
 * Rejects open redirects and cross-client `/clients/:id` when expectedClientId is set.
 */
export function safeReturnHref(
  value: string | null | undefined,
  options?: { expectedClientId?: string | null },
): string | null {
  if (!value) return null;
  const parsed = parseInternal(value.trim());
  if (!parsed) return null;

  const remapped = remapRetiredPath(parsed);
  if (remapped) {
    return safeReturnHref(remapped, options);
  }

  if (!isAllowedPath(parsed.pathname)) return null;

  const pathClient = parsed.pathname.match(/^\/clients\/([^/]+)/);
  const expected = options?.expectedClientId;
  if (expected && pathClient?.[1] && pathClient[1] !== expected) return null;

  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

export function returnLabelForHref(href: string): string {
  const parsed = parseInternal(href);
  if (!parsed) return 'Back';
  const path = parsed.pathname.replace(/\/+$/, '') || '/';
  if (path === '/tasks' || path === '/tasks/all') return 'Back to tasks';
  if (path.startsWith('/tasks/')) return 'Back to this task';
  if (path === '/overview' || path === '/' || path === '/home') return 'Back to Home';
  if (path.startsWith('/roster/')) return 'Back to Roster';
  if (path.startsWith('/home-care/')) return 'Back to home care';
  if (path.startsWith('/people/') || path.startsWith('/hr/')) return 'Back to people';
  if (path.startsWith('/me/')) return 'Back to My Statura';
  if (path.startsWith('/assessments')) return 'Back to assessments';
  if (path === '/care-plans' || path.startsWith('/care-plans/')) return 'Back to care plans';
  const tab = parsed.searchParams.get('tab');
  if (path.startsWith('/clients/')) {
    if (tab === 'budget') return 'Back to client Budget';
    if (tab === 'care-plans') return 'Back to client care plans';
    if (tab === 'assessments' || path.endsWith('/assessments')) return 'Back to client Assessments';
    if (tab === 'services') return 'Back to client Services';
    if (tab === 'sah-hub') return 'Back to client Journey';
    return 'Back to client Home';
  }
  return 'Back';
}

/** Restore only the owning register; the destination still enforces its normal access checks. */
export function safeRegisterReturnHref(
  value: string | null | undefined,
  register: '/clients' | '/people' | '/compliance/incidents' | '/compliance/complaints',
): string {
  const safe = safeReturnHref(value);
  const parsed = safe ? parseInternal(safe) : null;
  return parsed?.pathname === register ? safe! : register;
}

/** Incident records may return to the originating person's incident tab. */
export function safeIncidentReturnHref(value: string | null | undefined): string {
  const safe = safeReturnHref(value);
  const parsed = safe ? parseInternal(safe) : null;
  if (
    parsed &&
    /^\/clients\/[^/]+$/.test(parsed.pathname) &&
    parsed.searchParams.get('tab') === 'incidents'
  ) {
    return safe!;
  }
  return safeRegisterReturnHref(value, '/compliance/incidents');
}

/**
 * Stamp `returnTo` onto an outbound work href so Back/complete can restore
 * the originating surface. Does not overwrite an existing returnTo. Skips
 * worker `/me/*` hubs (those continue inside the self-service flow).
 */
export function withReturnTo(href: string, origin: string | null | undefined): string {
  const safeOrigin = safeReturnHref(origin);
  if (!safeOrigin || !href.startsWith('/') || href.startsWith('//')) return href;
  if (href === safeOrigin) return href;
  if (href.split(/[?#]/, 1)[0]?.startsWith('/me')) return href;
  const parsed = parseInternal(href);
  if (!parsed) return href;
  if (parsed.searchParams.has('returnTo')) return href;
  parsed.searchParams.set('returnTo', safeOrigin);
  return `${parsed.pathname}?${parsed.searchParams.toString()}${parsed.hash}`;
}

/** After marking a task complete: honour returnTo, else the inbox. */
export function postTaskCompleteHref(returnTo: string | null | undefined): string {
  return safeReturnHref(returnTo) ?? '/tasks';
}

/** Path + query the person is on now — used as returnTo when they open a job. */
export function currentReturnOrigin(pathname: string, search?: URLSearchParams | string): string {
  const qs = typeof search === 'string' ? search : (search?.toString() ?? '');
  return `${pathname}${qs ? `?${qs}` : ''}`;
}
